import { useCallback, useMemo } from 'react'

import { getClientEnv } from '@/env/client'
import type { Locale } from '@/i18n/routing'

import {
    createValueFormatters,
    undecidedFormatters,
    type FormatInputs,
    type ValueFormatters,
} from './format'
import { useFormatInputs } from './format-provider'
import type { TextValue } from './rich-text'
import { renderTemplate, type MessageTemplate, type TemplateValues } from './template'

interface FailurePolicy {
    readonly production: boolean
    readonly log: (message: string, context: Record<string, unknown>) => void
}

/** Logged from the client only: during server render the template has the values the
 *  server rendered its copy with, and a failure there is the copy set's to report. */
const logOnClient: FailurePolicy['log'] = (message, context) => {
    if (typeof window !== 'undefined') {
        getClientEnv().logger.withMetadata(context).error(message)
    }
}

/** The decision's formatters for a template in its locale, or — with no decision, or one
 *  made for another locale — those that refuse every kind a decision decides. */
const formattersFor = (locale: Locale, inputs: FormatInputs | undefined): ValueFormatters =>
    inputs?.locale === locale ? createValueFormatters(inputs) : undecidedFormatters(locale)

function renderWith<Values extends TemplateValues>(
    template: MessageTemplate<Values>,
    values: Values,
    format: ValueFormatters,
    {
        production = process.env.NODE_ENV === 'production',
        log = logOnClient,
    }: Partial<FailurePolicy> = {},
): string {
    return renderTemplate(template, values, format).match({
        Ok: (text) => text,
        Err: (failure) => {
            const missing = failure.cause === 'missing-variable' ? [...failure.missing] : undefined
            if (!production) {
                throw new Error(
                    `${template.file} "${template.key}" is unrenderable (${failure.cause}${
                        missing ? `: ${missing.join(', ')}` : ''
                    })`,
                )
            }
            log('Client template is unrenderable', {
                file: template.file,
                key: template.key,
                cause: failure.cause,
                ...(missing ? { missing } : {}),
            })
            return ''
        },
    })
}

/**
 * A client template's text for the client's values, each read as the kind its copy set
 * types it and formatted by the page's decision; with none, a kind that needs one is
 * refused. A template that cannot render fails the render outside production, as a copy
 * set's message does; in production it is empty text and an error naming the file and key.
 */
export function renderClientTemplate<Values extends TemplateValues>(
    template: MessageTemplate<Values>,
    values: Values,
    inputs?: FormatInputs,
    policy?: Partial<FailurePolicy>,
): string {
    return renderWith(template, values, formattersFor(template.locale, inputs), policy)
}

/**
 * A client template bound to the page frame's decision, called as the server calls a copy
 * set's template: `tripSummary(values).text`; none for a template the server decided absent.
 * Throws outside the frame's `FormatProvider`.
 */
export function useClientTemplate<Values extends TemplateValues>(
    template: MessageTemplate<Values>,
): (values: Values) => TextValue
export function useClientTemplate<Values extends TemplateValues>(
    template: MessageTemplate<Values> | undefined,
): ((values: Values) => TextValue) | undefined
export function useClientTemplate<Values extends TemplateValues>(
    template: MessageTemplate<Values> | undefined,
): ((values: Values) => TextValue) | undefined {
    const inputs = useFormatInputs()
    const locale = template?.locale
    const format = useMemo(
        () => (locale === undefined ? undefined : formattersFor(locale, inputs)),
        [locale, inputs],
    )
    const render = useCallback(
        (values: Values) => ({ text: renderWith(template!, values, format!) }),
        [template, format],
    )
    return template === undefined ? undefined : render
}
